import { createContext, useContext, useEffect, useState } from 'react';
import { UserPreferences, supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { dataManager } from '../lib/dataManager';

type PreferencesContextType = {
  preferences: UserPreferences | null;
  loading: boolean;
  isInitialized: boolean;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
  setTheme: (theme: 'light' | 'dark') => Promise<void>;
};

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences | null>(() => {
    // Synchronous initialization for logged-out state
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const urlTheme = params?.get('theme') as 'light' | 'dark' | null;
    const urlLang = params?.get('lang') as any;

    const savedTheme = urlTheme || localStorage.getItem('theme-preference');
    const systemDark = !savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme === 'dark' || systemDark ? 'dark' : 'light';

    const savedLanguage = urlLang || localStorage.getItem('language-preference');
    const initialLanguage = (savedLanguage as any) || 'en';
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const detectedHour12 = Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hour12;
    const initialTimeFormat = detectedHour12 ? '12h' : '24h';

    return {
      user_id: '',
      language: initialLanguage,
      theme: initialTheme,
      timezone: detectedTimezone,
      time_format: initialTimeFormat,
      created_at: new Date().toISOString(),
    };
  });
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useAuth();

  // Handle user login/logout
  useEffect(() => {
    if (user) {
      // User logged in - load from DB and subscribe to changes
      setIsInitialized(false);
      loadPreferences();

      const subscription = supabase
        .channel(`user_preferences:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_preferences',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            if (payload.new) {
              setPreferences(payload.new as UserPreferences);
            }
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    } else {
      // User logged out or initial state for guest
      setLoading(false);
      setIsInitialized(true);
    }
  }, [user]);

  useEffect(() => {
    if (preferences?.theme) {
      localStorage.setItem('theme-preference', preferences.theme);

      // Update browser favicon reactively
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = preferences.theme === 'dark' ? '/favicon_dark.png?v=2' : '/favicon.png?v=2';
      }
    }
    if (preferences?.language) {
      localStorage.setItem('language-preference', preferences.language);
    }
  }, [preferences?.theme, preferences?.language]);

  const setTheme = async (theme: 'light' | 'dark') => {
    // Update local state and storage immediately
    setPreferences(prev => prev ? { ...prev, theme } : { theme } as any);
    localStorage.setItem('theme-preference', theme);

    // If logged in, update DB
    if (user) {
      try {
        await supabase
          .from('user_preferences')
          .upsert({
            ...preferences,
            user_id: user.id,
            theme,
          });
      } catch (err) {
        console.error('Error syncing theme to DB:', err);
      }
    }
  };

  const loadPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      // If user has local theme/language preference but no DB entry, sync it now
      const localTheme = localStorage.getItem('theme-preference') as 'light' | 'dark';
      const localLanguage = localStorage.getItem('language-preference') as any;
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const detectedHour12 = Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hour12;
      const detectedTimeFormat = detectedHour12 ? '12h' : '24h';

      if (!data) {
        const newPrefs: UserPreferences = {
          user_id: user.id,
          language: localLanguage || 'en',
          theme: localTheme || 'light',
          timezone: detectedTimezone,
          time_format: detectedTimeFormat,
          created_at: new Date().toISOString(),
        };
        // Persist the default/local theme/language to DB immediately
        const { data: insertedData, error: insertError } = await supabase
          .from('user_preferences')
          .insert([newPrefs])
          .select()
          .single();

        if (!insertError && insertedData) {
          setPreferences(insertedData);
        } else {
          setPreferences(newPrefs);
        }
      } else {
        // DB has data - trust it over local storage (sync runs one-way DB -> Local on load)
        dataManager.updateUserPreferencesCache(user.id, data);
        setPreferences(data);

        // Ensure local storage matches DB
        if (data.theme) localStorage.setItem('theme-preference', data.theme);
        if (data.language) localStorage.setItem('language-preference', data.language);
        
        // Auto-update timezone behind the scenes if it has changed or is missing
        const needsTimezoneUpdate = data.timezone !== detectedTimezone;
        const needsTimeFormatUpdate = !data.time_format;

        if (needsTimezoneUpdate || needsTimeFormatUpdate) {
          const updatedData = { ...data, timezone: detectedTimezone, time_format: data.time_format || detectedTimeFormat };
          setPreferences(updatedData);
          dataManager.updateUserPreferencesCache(user.id, updatedData);
          
          supabase
            .from('user_preferences')
            .update({ 
              timezone: detectedTimezone,
              time_format: data.time_format || detectedTimeFormat 
            })
            .eq('user_id', user.id)
            .then(({ error }) => {
              if (error) console.warn('Failed to auto-update preferences', error);
            });
        }
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  };

  const updatePreferences = async (prefs: Partial<UserPreferences>) => {
    // Update state immediately using functional update to be safe
    setPreferences(prev => {
      const current = prev || { user_id: '', created_at: new Date().toISOString() } as UserPreferences;
      return { ...current, ...prefs };
    });

    // Update localStorage immediately
    if (prefs.language) {
      localStorage.setItem('language-preference', prefs.language);
    }
    if (prefs.theme) {
      localStorage.setItem('theme-preference', prefs.theme);
    }

    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          ...preferences,
          ...prefs,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      if (data.theme) {
        localStorage.setItem('theme-preference', data.theme);
      }
      dataManager.updateUserPreferencesCache(user.id, data);
      setPreferences(data);
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        loading,
        isInitialized,
        updatePreferences,
        setTheme,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
